import moment from 'moment-timezone';
import { useEffect, useRef, useState } from 'react';
import uPlot, { AlignedData, Options } from 'uplot';
import 'uplot/dist/uPlot.min.css';
import Tooltip from './tooltip';
import { TooltipData } from './types';

interface ChartProps {
  options: Options;
  data: AlignedData;
  width: string;
  height: string;
  timezone?: string;
  onTooltipData?: (data: TooltipData) => void;
}

const Chart = (props: ChartProps) => {
  const { options, data, width = '400px', height = '300px', timezone, onTooltipData } = props;
  const chartRef = useRef<HTMLDivElement>(null);
  const uplotRef = useRef<uPlot | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (chartRef.current) {
      // Destroy existing chart if it exists
      if (uplotRef.current) {
        uplotRef.current.destroy();
        uplotRef.current = null;
      }

      // Create new chart with updated data/options
      try {
        const chartOptions = {
          ...options,
          hooks: {
            ...options.hooks,
          },
        };

        uplotRef.current = new uPlot(chartOptions, data, chartRef.current);

        const handleMouseMove = (event: MouseEvent) => {
          const uOverDiv = uplotRef.current?.root.querySelector('.u-over') as HTMLElement;
          if (!uOverDiv || !uplotRef.current) return;

          const rect = uOverDiv.getBoundingClientRect();
          const mouseX = event.clientX - rect.left;
          const mouseY = event.clientY - rect.top;

          // Scale mouse position to match chart's coordinate system
          const scaleY = uplotRef.current.bbox.height / rect.height;
          // Account for chart's offset within the u-over div
          const scaledMouseY = mouseY * scaleY + uplotRef.current.bbox.top;

          // Check if mouse is within chart bounds
          if (
            mouseX < 0 ||
            mouseX > uplotRef.current.bbox.width ||
            mouseY < 0 ||
            mouseY > uplotRef.current.bbox.height
          ) {
            setShowTooltip(false);
            return;
          }

          // Check if mouse is hovering over excluded areas (legend, controls, etc.)
          const target = event.target as HTMLElement;
          const isOverExcludedArea =
            target.closest('.u-legend') ||
            target.closest('.u-legend tbody') ||
            target.closest('[class*="legend"]') ||
            target.closest('[class*="control"]') ||
            target.closest('button') ||
            target.closest('[class*="axis"]') ||
            target.closest('[role="button"]');

          if (isOverExcludedArea) {
            setShowTooltip(false);
            return;
          }

          const idx = uplotRef.current.posToIdx(mouseX);
          if (idx !== null && idx >= 0) {
            const timestamp = uplotRef.current.data[0][idx];
            const series = [];
            let closestSeriesIndex = -1;
            let minDistance = Infinity;

            // Get all series data at the current X position and find the closest one
            for (let i = 1; i < uplotRef.current.data.length; i++) {
              const value = uplotRef.current.data[i][idx];
              const seriesConfig = uplotRef.current.series[i];

              // Only include series that are visible (show: true)
              if (value !== undefined && value !== null && seriesConfig?.show !== false) {
                const seriesY = uplotRef.current.valToPos(value, 'y', true);
                // uPlot Y coordinate system: 0 at top, increases downward
                // Higher data values should be closer to top (lower Y values)
                const distance = Math.abs(scaledMouseY - seriesY);

                if (distance < minDistance) {
                  minDistance = distance;
                  closestSeriesIndex = i;
                }

                series.push({
                  label: seriesConfig?.label || `Series ${i}`,
                  value: value,
                  color:
                    typeof seriesConfig?.stroke === 'function'
                      ? seriesConfig.stroke(uplotRef.current, i)
                      : seriesConfig?.stroke || '#000',
                  distance: distance,
                });
              }
            }

            if (series.length > 0) {
              // Show only the closest series when mouse is close to a line
              const threshold = 10; // pixels - adjust this value to control sensitivity
              const displaySeries =
                minDistance < threshold && closestSeriesIndex > 0
                  ? series.filter((s) => Math.abs(s.distance - minDistance) < 0.001)
                  : series; // Show all series if not close to any line

              // Calculate tooltip position with viewport boundary detection
              const padding = 10; // Padding from viewport edge

              // Get actual tooltip dimensions if available, otherwise use estimates
              let tooltipWidth = 200; // Default width
              let tooltipHeight = 100; // Default height

              if (tooltipRef.current) {
                const tooltipRect = tooltipRef.current.getBoundingClientRect();
                tooltipWidth = tooltipRect.width;
                tooltipHeight = tooltipRect.height;
              }

              let tooltipX = mouseX;
              let tooltipY = mouseY;

              // Always position tooltip to the right of cursor first
              tooltipX = mouseX + 100;

              // Only move to the left if it would go out of bounds on the right
              if (tooltipX + tooltipWidth + padding > rect.width) {
                tooltipX = mouseX - tooltipWidth - 15;
              }

              // If it still goes out of bounds on the left, clamp it to the left edge
              if (tooltipX < padding) {
                tooltipX = padding;
              }

              // Position tooltip below cursor by default
              tooltipY = mouseY + 40;

              // Check if tooltip would go out of bounds on the bottom
              if (tooltipY + tooltipHeight + padding > rect.height) {
                // Position above cursor instead
                tooltipY = mouseY - tooltipHeight - 15;
              }

              // Ensure tooltip doesn't go out of bounds on the top
              if (tooltipY < padding) {
                tooltipY = padding;
              }

              const tooltipInfo = {
                series: displaySeries,
                x: tooltipX,
                y: tooltipY,
                timestamp: timezone
                  ? moment.unix(timestamp).tz(timezone).format('YYYY-MM-DD HH:mm:ss')
                  : new Date(timestamp * 1000).toLocaleString(),
              };

              setTooltipData(tooltipInfo);
              setShowTooltip(true);

              if (onTooltipData) {
                onTooltipData(tooltipInfo);
              }
            }
          }
        };

        const handleMouseLeave = () => {
          setShowTooltip(false);
        };

        const currentChartRef = chartRef.current;
        currentChartRef.addEventListener('mousemove', handleMouseMove);
        currentChartRef.addEventListener('mouseleave', handleMouseLeave);

        return () => {
          if (currentChartRef) {
            currentChartRef.removeEventListener('mousemove', handleMouseMove);
            currentChartRef.removeEventListener('mouseleave', handleMouseLeave);
          }
        };
      } catch (error) {
        console.error('Error creating chart:', error);
      }
    }

    return () => {
      if (uplotRef.current) {
        uplotRef.current.destroy();
        uplotRef.current = null;
      }
    };
  }, [options, data, timezone, onTooltipData]);

  return (
    <div style={{ position: 'relative', width: width, height: height }}>
      <div ref={chartRef} style={{ width: '100%', height: '100%' }} />
      <Tooltip ref={tooltipRef} data={tooltipData} visible={showTooltip} />
    </div>
  );
};

export default Chart;
